/**
 * Schemas para actividades tab + sub-tabs condicionales (data-model V1.1 §3.4-3.7).
 *
 * Master tab `actividades` + sub-tabs CONDICIONALES por tipo:
 * - tipo=servicio → actividades_servicio
 * - tipo=reclutamiento → actividades_reclutamiento
 * - tipo=evento_internacional → actividades_internacional
 * - Otros tipos (junta_semanal, evento, capacitacion) → no sub-tab
 *
 * PII tier: mayormente Bajo. created_by es Alto (FK usuarios).
 */
import { z } from 'zod';
import { VersionedFieldsSchema, listResponseSchema } from './common.js';

// ─── Enums ─────────────────────────────────────────────────────────────────

export const TipoActividadSchema = z.enum([
  'junta_semanal',
  'evento',
  'servicio',
  'reclutamiento',
  'capacitacion',
  'evento_internacional',
]);
export type TipoActividad = z.infer<typeof TipoActividadSchema>;

export const TipoServicioSchema = z.enum(['comunidad', 'ambiental', 'social', 'educativo', 'otro']);
export type TipoServicio = z.infer<typeof TipoServicioSchema>;

export const TipoEventoInternacionalSchema = z.enum([
  'intercambio',
  'congreso',
  'campamento',
  'competencia',
  'otro',
]);
export type TipoEventoInternacional = z.infer<typeof TipoEventoInternacionalSchema>;

// ─── Master tab `actividades` (V1.1 §3.4) ──────────────────────────────────

export const ActividadSchema = VersionedFieldsSchema.extend({
  nombre: z.string().min(3),
  lugar: z.string().min(1),
  fecha_actividad: z.string().date(),
  fecha_registro: z.string().date(),
  tipo: TipoActividadSchema,
  rama: z.string().min(1),
  participantes: z.number().int().min(0),
  beneficiarios: z.number().int().min(0).nullable(),
  horas: z.number().min(0),
  evidencias_urls: z.array(z.string().url()).nullable(),
  fotos: z.array(z.string().url()).max(5).nullable(),
  quarter: z.string().regex(/^\d{4}-Q[1-4]$/),
});
export type Actividad = z.infer<typeof ActividadSchema>;

/** Create payload — server-computed fields excluded (entity_id, version_id, quarter, etc.) */
export const ActividadCreatePayloadSchema = z.object({
  nombre: ActividadSchema.shape.nombre,
  lugar: ActividadSchema.shape.lugar,
  fecha_actividad: ActividadSchema.shape.fecha_actividad,
  fecha_registro: ActividadSchema.shape.fecha_registro,
  tipo: ActividadSchema.shape.tipo,
  rama: ActividadSchema.shape.rama,
  participantes: ActividadSchema.shape.participantes,
  beneficiarios: ActividadSchema.shape.beneficiarios.optional(),
  horas: ActividadSchema.shape.horas,
  evidencias_urls: ActividadSchema.shape.evidencias_urls.optional(),
  fotos: ActividadSchema.shape.fotos.optional(),
});
export type ActividadCreatePayload = z.infer<typeof ActividadCreatePayloadSchema>;

export const ActividadUpdatePayloadSchema = ActividadCreatePayloadSchema.partial();
export type ActividadUpdatePayload = z.infer<typeof ActividadUpdatePayloadSchema>;

// ─── Sub-tab `actividades_servicio` (V1.1 §3.5) ────────────────────────────

export const ActividadServicioSchema = VersionedFieldsSchema.extend({
  tipo_servicio: TipoServicioSchema,
  descripcion_servicio: z.string().min(10),
  comunidad_beneficiada: z.string().nullable(),
  aliados: z.array(z.string()).nullable(),
});
export type ActividadServicio = z.infer<typeof ActividadServicioSchema>;

export const ActividadServicioPayloadSchema = z.object({
  tipo_servicio: ActividadServicioSchema.shape.tipo_servicio,
  descripcion_servicio: ActividadServicioSchema.shape.descripcion_servicio,
  comunidad_beneficiada: ActividadServicioSchema.shape.comunidad_beneficiada.optional(),
  aliados: ActividadServicioSchema.shape.aliados.optional(),
});
export type ActividadServicioPayload = z.infer<typeof ActividadServicioPayloadSchema>;

// ─── Sub-tab `actividades_reclutamiento` (V1.1 §3.6) ───────────────────────

export const ActividadReclutamientoSchema = VersionedFieldsSchema.extend({
  lugar_reclutamiento: z.string().min(1),
  prospectos_contactados: z.number().int().min(0),
  prospectos_interesados: z.number().int().min(0),
  prospectos_inscritos: z.number().int().min(0),
  material_distribuido: z.string().nullable(),
});
export type ActividadReclutamiento = z.infer<typeof ActividadReclutamientoSchema>;

export const ActividadReclutamientoPayloadSchema = z.object({
  lugar_reclutamiento: ActividadReclutamientoSchema.shape.lugar_reclutamiento,
  prospectos_contactados: ActividadReclutamientoSchema.shape.prospectos_contactados,
  prospectos_interesados: ActividadReclutamientoSchema.shape.prospectos_interesados,
  prospectos_inscritos: ActividadReclutamientoSchema.shape.prospectos_inscritos,
  material_distribuido: ActividadReclutamientoSchema.shape.material_distribuido.optional(),
});
export type ActividadReclutamientoPayload = z.infer<typeof ActividadReclutamientoPayloadSchema>;

// ─── Sub-tab `actividades_internacional` (V1.1 §3.7) ───────────────────────

export const ActividadInternacionalSchema = VersionedFieldsSchema.extend({
  pais_destino: z.string().min(1),
  ciudad_destino: z.string().nullable(),
  organizacion_anfitriona: z.string().nullable(),
  tipo_evento_internacional: TipoEventoInternacionalSchema,
  participantes_internacionales: z.number().int().min(0),
});
export type ActividadInternacional = z.infer<typeof ActividadInternacionalSchema>;

export const ActividadInternacionalPayloadSchema = z.object({
  pais_destino: ActividadInternacionalSchema.shape.pais_destino,
  ciudad_destino: ActividadInternacionalSchema.shape.ciudad_destino.optional(),
  organizacion_anfitriona: ActividadInternacionalSchema.shape.organizacion_anfitriona.optional(),
  tipo_evento_internacional: ActividadInternacionalSchema.shape.tipo_evento_internacional,
  participantes_internacionales: ActividadInternacionalSchema.shape.participantes_internacionales,
});
export type ActividadInternacionalPayload = z.infer<typeof ActividadInternacionalPayloadSchema>;

// ─── Discriminated union — full create payload per tipo ────────────────────

/**
 * Payload completo de POST /v1/actividades — incluye subtype condicional según tipo.
 *
 * tipo=servicio → requires `subtype_servicio`
 * tipo=reclutamiento → requires `subtype_reclutamiento`
 * tipo=evento_internacional → requires `subtype_internacional`
 * otros tipos → NO subtype
 */
export const ActividadFullCreatePayloadSchema = z.discriminatedUnion('tipo', [
  ActividadCreatePayloadSchema.extend({
    tipo: z.literal('servicio'),
    subtype_servicio: ActividadServicioPayloadSchema,
  }),
  ActividadCreatePayloadSchema.extend({
    tipo: z.literal('reclutamiento'),
    subtype_reclutamiento: ActividadReclutamientoPayloadSchema,
  }),
  ActividadCreatePayloadSchema.extend({
    tipo: z.literal('evento_internacional'),
    subtype_internacional: ActividadInternacionalPayloadSchema,
  }),
  ActividadCreatePayloadSchema.extend({ tipo: z.literal('junta_semanal') }),
  ActividadCreatePayloadSchema.extend({ tipo: z.literal('evento') }),
  ActividadCreatePayloadSchema.extend({ tipo: z.literal('capacitacion') }),
]);
export type ActividadFullCreatePayload = z.infer<typeof ActividadFullCreatePayloadSchema>;

// ─── Read response — actividad + subtype embedded ──────────────────────────

export const ActividadWithSubtypeSchema = z.object({
  actividad: ActividadSchema,
  servicio: ActividadServicioSchema.nullable(),
  reclutamiento: ActividadReclutamientoSchema.nullable(),
  internacional: ActividadInternacionalSchema.nullable(),
});
export type ActividadWithSubtype = z.infer<typeof ActividadWithSubtypeSchema>;

// ─── List response ─────────────────────────────────────────────────────────

export const ActividadListResponseSchema = listResponseSchema(ActividadSchema);
export type ActividadListResponse = z.infer<typeof ActividadListResponseSchema>;
